import type { GoalTypeOption } from "@repo/shared/types";
import { ChoiceCards } from "@/components/ui/choice-cards";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./goal-type-picker.i18n";

type GoalTypePickerProps = {
  /** Scopes the control ids — a multi-goal flow mounts one picker per goal. */
  idPrefix: string;
  /** Already filtered to what this goal may show (QL-36). Empty renders nothing. */
  goalTypes: GoalTypeOption[];
  value: string;
  onValueChange: (goalTypeId: string) => void;
  disabled?: boolean;
};

/**
 * QP-187 — the required goal-type picker shared by the goal page's details
 * widget and the Goal List modal: tiles when any type has an icon, radio rows
 * otherwise. Presentational — the owner holds the value and decides what a
 * selection does (the widget autosaves, the modal waits for Save).
 */
export function GoalTypePicker({
  idPrefix,
  goalTypes,
  value,
  onValueChange,
  disabled = false,
}: GoalTypePickerProps) {
  const localize = useLocalize();
  const t = useT(T);
  if (goalTypes.length === 0) return null;
  const useTiles = goalTypes.some((gt) => !!gt.iconSvg);
  const labelId = `${idPrefix}-goal-type-label`;

  return (
    <div className="space-y-2">
      <Label id={labelId}>
        {t.goalType} <span className="text-destructive">*</span>
      </Label>
      {useTiles ? (
        <ChoiceCards
          aria-labelledby={labelId}
          options={goalTypes.map((gt) => ({
            id: gt.id,
            label: localize(gt.labelI18n),
            iconSvg: gt.iconSvg,
          }))}
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
        />
      ) : (
        <RadioGroup value={value || undefined} onValueChange={onValueChange} disabled={disabled}>
          {goalTypes.map((gt) => (
            <OptionRow key={gt.id}>
              <RadioGroupItem id={`${idPrefix}-goal-type-${gt.id}`} value={gt.id} />
              <Label htmlFor={`${idPrefix}-goal-type-${gt.id}`} className="font-normal">
                {localize(gt.labelI18n)}
              </Label>
            </OptionRow>
          ))}
        </RadioGroup>
      )}
    </div>
  );
}
